import type { ReactNode } from 'react'

import { RadioGroup } from '@headlessui/react'
import classNames from 'classnames'
import { AlertCircle, Check, Minus, Plus } from 'lucide-react'

/* Shared building blocks for the Create tournament steps. Theme tokens only. */

export const FOCUS_RING =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-btn-fancy-primary-end focus-visible:ring-offset-2 focus-visible:ring-offset-light-card dark:focus-visible:ring-offset-dark-card'

export const inputClass = (hasError?: boolean) =>
  classNames(
    'w-full rounded-lg border bg-light-bg-secondary dark:bg-dark-bg px-3.5 py-2.5 text-sm',
    'text-light-typo-primary dark:text-dark-typo-primary placeholder:text-light-typo-gray dark:placeholder:text-dark-typo-gray',
    'transition-colors focus:outline-none focus:ring-2',
    hasError
      ? 'border-red focus:ring-red/30'
      : 'border-light-border-primary dark:border-dark-border-primary hover:border-dark-gray-primary focus:border-btn-fancy-primary-end focus:ring-btn-fancy-primary-end/25',
  )

export const StepHeading = ({ title, intro, id }: { title: string; intro: string; id?: string }) => (
  <header className="mb-6">
    <h2
      id={id}
      tabIndex={-1}
      className="font-display text-2xl sm:text-[28px] font-bold leading-tight text-light-typo-primary dark:text-dark-typo-primary focus:outline-none"
    >
      {title}
    </h2>
    <p className="mt-1 text-sm text-light-typo-gray dark:text-dark-typo-gray">{intro}</p>
  </header>
)

export const Section = ({
  title,
  description,
  children,
  className,
}: {
  title?: string
  description?: ReactNode
  children: ReactNode
  className?: string
}) => (
  <section className={classNames('py-5 first:pt-0 border-t first:border-t-0 border-light-border-primary/60 dark:border-dark-border-primary/60', className)}>
    {title && <h3 className="text-[15px] font-semibold text-light-typo-primary dark:text-dark-typo-primary">{title}</h3>}
    {description && <p className="mt-0.5 text-xs leading-5 text-light-typo-gray dark:text-dark-typo-gray">{description}</p>}
    <div className={classNames(title || description ? 'mt-3' : '')}>{children}</div>
  </section>
)

export const FieldLabel = ({ htmlFor, children, optional }: { htmlFor?: string; children: ReactNode; optional?: boolean }) => (
  <label htmlFor={htmlFor} className="mb-1.5 flex items-baseline gap-2 text-sm font-semibold text-light-typo-primary dark:text-dark-typo-primary">
    {children}
    {optional && <span className="text-xs font-normal text-light-typo-gray dark:text-dark-typo-gray">Optional</span>}
  </label>
)

export const Help = ({ children, id }: { children: ReactNode; id?: string }) => (
  <p id={id} className="mt-1.5 text-xs leading-5 text-light-typo-gray dark:text-dark-typo-gray">
    {children}
  </p>
)

export const FieldError = ({ message, id }: { message?: string; id?: string }) =>
  message ? (
    <p id={id} role="alert" className="mt-1.5 flex items-start gap-1.5 text-xs font-medium leading-5 text-red">
      <AlertCircle size={14} className="mt-0.5 flex-shrink-0" aria-hidden />
      {message}
    </p>
  ) : null

export const Banner = ({ tone = 'info', children }: { tone?: 'info' | 'error'; children: ReactNode }) => (
  <div
    role={tone === 'error' ? 'alert' : undefined}
    className={classNames(
      'flex items-start gap-2 rounded-lg border px-3 py-2.5 text-xs leading-5',
      tone === 'error'
        ? 'border-red/50 bg-red/10 text-red'
        : 'border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg text-light-typo-gray dark:text-dark-typo-gray',
    )}
  >
    {tone === 'error' && <AlertCircle size={14} className="mt-0.5 flex-shrink-0" aria-hidden />}
    <div>{children}</div>
  </div>
)

/** Accessible on/off switch row. */
export const SwitchRow = ({
  id,
  checked,
  onChange,
  label,
  description,
}: {
  id: string
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  description?: string
}) => (
  <div className="flex items-start justify-between gap-4">
    <div className="min-w-0">
      <label htmlFor={id} className="cursor-pointer text-sm font-semibold text-light-typo-primary dark:text-dark-typo-primary">
        {label}
      </label>
      {description && <p id={`${id}-desc`} className="mt-0.5 text-xs leading-5 text-light-typo-gray dark:text-dark-typo-gray">{description}</p>}
    </div>
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-describedby={description ? `${id}-desc` : undefined}
      onClick={() => onChange(!checked)}
      className={classNames(
        'relative mt-0.5 inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors',
        checked ? 'bg-gradient-to-r from-btn-fancy-primary-start to-btn-fancy-primary-end' : 'bg-light-border-primary dark:bg-dark-border-primary',
        FOCUS_RING,
      )}
    >
      <span
        aria-hidden
        className={classNames(
          'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform motion-reduce:transition-none',
          checked ? 'translate-x-[22px]' : 'translate-x-0.5',
        )}
      />
    </button>
  </div>
)

export type ChipOption<T extends string> = { value: T; label: string; disabled?: boolean; hint?: string }

/** Compact single-choice chips (radio group semantics, arrow-key navigation). */
export function ChipGroup<T extends string>({
  label,
  hideLabel,
  options,
  value,
  onChange,
  invalid,
  describedBy,
  className,
}: {
  label: string
  hideLabel?: boolean
  options: ChipOption<T>[]
  value?: T | ''
  onChange: (value: T) => void
  invalid?: boolean
  describedBy?: string
  className?: string
}) {
  return (
    <RadioGroup value={value || null} onChange={(v: T) => onChange(v)} aria-describedby={describedBy}>
      <RadioGroup.Label className={hideLabel ? 'sr-only' : 'mb-1.5 block text-sm font-semibold text-light-typo-primary dark:text-dark-typo-primary'}>
        {label}
      </RadioGroup.Label>
      <div className={classNames('flex flex-wrap gap-2', className)} aria-invalid={invalid || undefined}>
        {options.map((option) => (
          <RadioGroup.Option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            title={option.hint}
            className={({ checked, disabled }) =>
              classNames(
                'inline-flex min-w-[3.25rem] cursor-pointer select-none items-center justify-center rounded-lg border px-3 py-2 text-sm font-semibold tabular-nums transition-colors',
                FOCUS_RING,
                disabled && 'cursor-not-allowed opacity-40',
                checked
                  ? 'border-btn-fancy-primary-end/70 bg-btn-fancy-primary-end/10 text-light-typo-primary dark:text-dark-typo-primary'
                  : invalid
                    ? 'border-red/60 text-light-typo-gray dark:text-dark-typo-gray hover:text-light-typo-primary dark:hover:text-dark-typo-primary'
                    : 'border-light-border-primary dark:border-dark-border-primary text-light-typo-gray dark:text-dark-typo-gray hover:border-dark-gray-primary hover:text-light-typo-primary dark:hover:text-dark-typo-primary',
              )
            }
          >
            {option.label}
          </RadioGroup.Option>
        ))}
      </div>
    </RadioGroup>
  )
}

/** Large selectable card used for the important choices (radio group semantics). */
export function ChoiceCards<T extends string>({
  label,
  options,
  value,
  onChange,
  columns = 'sm:grid-cols-3',
  invalid,
}: {
  label: string
  options: { value: T; title: string; description: string; meta?: string; icon?: ReactNode; disabled?: boolean; badge?: string }[]
  value?: T | ''
  onChange: (value: T) => void
  columns?: string
  invalid?: boolean
}) {
  return (
    <RadioGroup value={value || null} onChange={(v: T) => onChange(v)}>
      <RadioGroup.Label className="sr-only">{label}</RadioGroup.Label>
      <div className={classNames('grid grid-cols-1 gap-3', columns)}>
        {options.map((option) => (
          <RadioGroup.Option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            className={({ checked, disabled }) =>
              classNames(
                'relative flex cursor-pointer flex-col rounded-xl border p-4 text-left transition-colors',
                FOCUS_RING,
                disabled && 'cursor-not-allowed opacity-50',
                checked
                  ? 'border-btn-fancy-primary-end/70 bg-btn-fancy-primary-end/[0.07]'
                  : invalid
                    ? 'border-red/60 bg-light-bg-secondary dark:bg-dark-bg'
                    : 'border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg hover:border-dark-gray-primary',
              )
            }
          >
            {({ checked }) => (
              <>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    {option.icon && (
                      <span
                        aria-hidden
                        className={classNames(
                          'flex h-8 w-8 items-center justify-center rounded-lg',
                          checked ? 'bg-btn-fancy-primary-end/15 text-green' : 'bg-light-gray-light dark:bg-dark-bg-secondary text-light-typo-gray dark:text-dark-typo-gray',
                        )}
                      >
                        {option.icon}
                      </span>
                    )}
                    <RadioGroup.Label as="span" className="font-display text-lg font-bold leading-tight text-light-typo-primary dark:text-dark-typo-primary">
                      {option.title}
                    </RadioGroup.Label>
                  </div>
                  <span
                    aria-hidden
                    className={classNames(
                      'mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border',
                      checked
                        ? 'border-transparent bg-gradient-to-br from-btn-fancy-primary-start to-btn-fancy-primary-end text-black'
                        : 'border-light-border-primary dark:border-dark-border-primary',
                    )}
                  >
                    {checked && <Check size={12} strokeWidth={3} />}
                  </span>
                </div>
                <RadioGroup.Description as="span" className="mt-2 block text-xs leading-5 text-light-typo-gray dark:text-dark-typo-gray">
                  {option.description}
                </RadioGroup.Description>
                {(option.meta || option.badge) && (
                  <span className="mt-3 flex flex-wrap items-center gap-2 text-[11px] font-medium text-light-typo-gray dark:text-dark-typo-gray">
                    {option.badge && (
                      <span className="rounded border border-yellow/40 px-1.5 py-0.5 text-yellow">{option.badge}</span>
                    )}
                    {option.meta}
                  </span>
                )}
              </>
            )}
          </RadioGroup.Option>
        ))}
      </div>
    </RadioGroup>
  )
}

/** Small numeric stepper with − / + buttons. */
export const NumberStepper = ({
  id,
  value,
  min,
  max,
  onChange,
  label,
  suffix,
}: {
  id: string
  value: number
  min: number
  max: number
  onChange: (value: number) => void
  label: string
  suffix?: string
}) => {
  const clamp = (n: number) => Math.min(max, Math.max(min, n))
  const btn = classNames(
    'flex h-9 w-9 items-center justify-center rounded-lg border border-light-border-primary dark:border-dark-border-primary text-light-typo-primary dark:text-dark-typo-primary transition-colors hover:border-dark-gray-primary disabled:cursor-not-allowed disabled:opacity-40',
    FOCUS_RING,
  )
  return (
    <div className="flex items-center gap-2">
      <button type="button" className={btn} onClick={() => onChange(clamp(value - 1))} disabled={value <= min} aria-label={`Fewer ${label.toLowerCase()}`}>
        <Minus size={14} aria-hidden />
      </button>
      <input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={value}
        onChange={(e) => {
          const n = parseInt(e.target.value)
          if (!Number.isNaN(n)) onChange(clamp(n))
        }}
        // inputClass() is w-full; drop it so the field stays compact between the buttons.
        className={classNames(inputClass().replace('w-full ', ''), 'w-16 flex-shrink-0 px-2 text-center font-display text-lg font-bold tabular-nums')}
      />
      <button type="button" className={btn} onClick={() => onChange(clamp(value + 1))} disabled={value >= max} aria-label={`More ${label.toLowerCase()}`}>
        <Plus size={14} aria-hidden />
      </button>
      {suffix && <span className="whitespace-nowrap text-xs text-light-typo-gray dark:text-dark-typo-gray">{suffix}</span>}
    </div>
  )
}
